import { useTheme } from '@mui/material/styles';
import { AxisLabel, ChartTooltip, useWidgetContext } from '@procureai/ui-mui';
import { ReactNode, ReactElement, ComponentType, useMemo, ComponentProps } from 'react';
import { XAxis, YAxis, CartesianGrid, Legend, XAxisProps } from 'recharts';

import { Root, baseChartClasses as classes } from './BaseChart.styled';
import { AxisConfig, ItemConfig } from './BaseChart.types';
import { getColorShades, X_AXIS, Y_AXIS, MARGIN, getTooltipFormatter } from './BaseChart.util';

type LegendProps = ComponentProps<typeof Legend>;

export interface BaseChartProps {
  data: any[];
  dataKey: string;
  dataConfig?: ItemConfig;
  yAxis?: AxisConfig;
  tooltip?: boolean;
  legend?: boolean;
  chartComponent: ComponentType<any>;
  children: (props: {
    size?: number;
    formatter?: (value: string | number) => ReactNode;
    colors: string[];
    className?: string;
  }) => ReactElement<any>;
}

export const BaseChart = ({
  data,
  dataKey,
  yAxis,
  dataConfig,
  tooltip,
  legend,
  chartComponent: ChartComponent,
  children,
  ...containerProps
}: BaseChartProps) => {
  const theme = useTheme();
  const { hovering } = useWidgetContext();

  const colors: any = useMemo(() => {
    return hovering ? theme.palette.chartPrimary : theme.palette.grey;
  }, [theme, hovering]);

  const colorShades = useMemo(() => {
    return getColorShades(1, colors);
  }, [colors]);

  const tooltipFormatter: any = useMemo(() => {
    return getTooltipFormatter(yAxis, dataConfig);
  }, [dataConfig, yAxis]);

  const legendFormatter: LegendProps['formatter'] = useMemo(() => {
    return (value) => {
      return dataConfig?.label || value;
    };
  }, [dataConfig]);

  const xAxis: Partial<XAxisProps> | undefined = useMemo(() => {
    return dataConfig?.formatter ? { tickFormatter: dataConfig.formatter as any } : undefined;
  }, [dataConfig]);

  const yAxisProps = { ...Y_AXIS, ...yAxis };

  return (
    <Root height={350} width="100%" {...containerProps}>
      <ChartComponent data={data} barGap={4} margin={MARGIN}>
        <CartesianGrid vertical={false} />
        <XAxis dataKey={dataKey} {...X_AXIS} {...xAxis} />
        <YAxis
          {...yAxisProps}
          label={
            yAxisProps.label
              ? ((<AxisLabel value={yAxisProps.label} className={classes.axisLabel} />) as any)
              : undefined
          }
        />
        {tooltip && <ChartTooltip hovering={hovering} cursor={true} formatter={tooltipFormatter} />}
        {legend && <Legend formatter={legendFormatter} iconSize={10} />}
        {children({
          colors: colorShades,
        })}
      </ChartComponent>
    </Root>
  );
};
